import { isCancelledError } from "@tanstack/react-query"
import posthog from "posthog-js"
import {
	AccessDeniedError,
	type HttpError,
	isAbortError,
	isHttpError,
	isNetworkError,
} from "@/shared/api/errors"

/**
 * Замена posthog.captureException в местах, куда может долететь сетевой сбой.
 * Имя не reportError: так зовётся глобальный DOM-метод, и забытый импорт
 * молча уходил бы в него вместо этой функции.
 *
 * Сетевую ошибку в error tracking слать бессмысленно: движок бросает её из
 * нативного кода, стека нет, чинить в событии нечего — остаётся issue без
 * фреймов, который невозможно закрыть. Поэтому она уходит обычным событием.
 *
 * phase: surfaced — сбой пережил ретраи транспорта и дошёл до пользователя;
 * phase: transport (noteNetworkFailure в shared/api/http/telemetry.ts) — все сбои подряд.
 * Отношение surfaced к transport показывает, справляются ли повторы.
 *
 * По той же причине отдельным событием уходит 4xx: отказ бэкенда, в коде
 * фронта чинить нечего, а issue он заводит незакрываемый.
 */
export function captureError(
	error: unknown,
	props: { scope: string } & Record<string, unknown>,
): void {
	if (isAbortError(error) || isCancelledError(error)) return
	if (error instanceof AccessDeniedError) return
	if (isNetworkError(error)) {
		posthog.capture("network_failure", {
			phase: "surfaced",
			scope: props.scope,
			http_path: error.path,
			http_method: error.method,
		})
		return
	}
	if (isHttpError(error) && error.status < 500) {
		posthog.capture("http_client_error", {
			scope: props.scope,
			http_status: error.status,
			http_path: error.path,
			http_method: error.method,
			http_info: validationInfo(error),
		})
		return
	}
	if (error instanceof Error) {
		posthog.captureException(error, props)
		return
	}
	// Wrapped by hand: PostHog would otherwise synthesise a stackless «Primitive
	// value captured as exception» issue with no scope in its title, merging
	// unrelated call sites into one unfixable issue.
	const raw = safeStringify(error)
	posthog.captureException(
		new Error(`Non-error thrown in ${props.scope}: ${raw}`),
		{ ...props, raw_value: raw },
	)
}

const REDACTED_KEYS = new Set(["input", "value", "ctx"])

function validationInfo({ status, info }: HttpError): string | undefined {
	if (status !== 422 || info === undefined) return undefined
	try {
		return JSON.stringify(info, (key, value: unknown) => {
			if (REDACTED_KEYS.has(key)) return null
			return value
		}).slice(0, 500)
	} catch {
		return Object.prototype.toString.call(info)
	}
}

/** Falls back to a tag string when the value cannot be serialised. */
function safeStringify(value: unknown): string {
	try {
		if (typeof value === "object" && value !== null) {
			return JSON.stringify(value)
		}
		return String(value)
	} catch {
		return Object.prototype.toString.call(value)
	}
}
